import { createFileRoute, useNavigate } from '@tanstack/react-router';

type DemoRole = 'admin' | 'parent' | 'faculty' | 'dancer';

const demoAccounts: {
  role: DemoRole;
  label: string;
  email: string;
  description: string;
  path: string;
}[] = [
  {
    role: 'admin',
    label: 'Studio admin',
    email: 'admin@example.test',
    description: 'Studio overview, families, dancers, classes, billing, reports.',
    path: '/admin',
  },
  {
    role: 'parent',
    label: 'Parent',
    email: 'parent@example.test',
    description: 'Family schedule, dancer progress, messages, billing, tickets.',
    path: '/parent',
  },
  {
    role: 'faculty',
    label: 'Faculty',
    email: 'faculty@example.test',
    description: 'Assigned classes, attendance, and production information.',
    path: '/faculty',
  },
  {
    role: 'dancer',
    label: 'Dancer',
    email: 'dancer@example.test',
    description: 'Standalone dancer schedule, routines, and competitions.',
    path: '/dancer',
  },
];

export const Route = createFileRoute('/dev/demo')({
  component: DemoAccountsPage,
});

function DemoAccountsPage() {
  const navigate = useNavigate();
  const isProd = import.meta.env.PROD;

  function signIn(role: DemoRole, path: string, email: string) {
    if (isProd) return;

    const session = {
      access_token: `test-${role}-token`,
      token_type: 'bearer',
      expires_in: 3600,
      expires_at: Math.floor(Date.now() / 1000) + 3600,
      refresh_token: `test-${role}-refresh`,
      user: {
        id: role === 'faculty' ? 'user-faculty' : `user-${role}`,
        aud: 'authenticated',
        role: 'authenticated',
        email,
        app_metadata: { role, family_id: 'fam-1' },
        user_metadata: {},
        created_at: new Date().toISOString(),
      },
    };

    window.localStorage.setItem('pirouette.auth', JSON.stringify(session));
    void navigate({ to: path });
  }

  return (
    <main className="min-h-dvh bg-[var(--color-surface)] px-6 py-10">
      <section className="mx-auto max-w-5xl">
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Local demo
        </p>
        <h1 className="mt-2 font-[var(--font-display)] tracking-[var(--tracking-tight)] text-[var(--text-display)]">
          Try Pirouette by role
        </h1>
        <p className="mt-4 max-w-2xl text-[var(--color-ink-muted)] text-[var(--text-lg)]">
          These are local demo sessions for desktop testing. They do not create production users,
          send emails, or touch live studio data.
        </p>

        {isProd ? (
          <div className="surface mt-8 p-5">
            Demo accounts are disabled in production builds.
          </div>
        ) : (
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {demoAccounts.map((account) => (
              <button
                key={account.role}
                type="button"
                className="surface text-left transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-lg)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                onClick={() => signIn(account.role, account.path, account.email)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="font-[var(--font-display)] text-[var(--text-2xl)]">
                        {account.label}
                      </h2>
                      <p className="mt-1 text-[var(--color-ink-subtle)] text-[var(--text-sm)]">
                        {account.email}
                      </p>
                    </div>
                    <span className="badge badge-info">{account.role}</span>
                  </div>
                  <p className="mt-5 text-[var(--color-ink-muted)]">{account.description}</p>
                  <p className="mt-6 font-medium text-[var(--color-accent)] text-[var(--text-sm)]">
                    Open {account.label} view
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
